<template>
  <div>
    <b-card class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MOVEMENT_ITEMS") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-permission="['stock-actions-create']"
            to="receiving"
            class="btn btn-light-success font-weight-bolder mr-2"
            ><i class="flaticon2-plus"></i>
            {{ $t("STOCK_RECEIVING") }}
          </router-link>
          <router-link
            v-permission="['stock-actions-create']"
            to="returning"
            class="btn btn-light-success font-weight-bolder mr-2"
            ><i class="flaticon2-plus"></i>
            {{ $t("STOCK_RETURNING") }}
          </router-link>
          <router-link
            v-permission="['stock-actions-create']"
            to="scrapping"
            class="btn btn-light-success font-weight-bolder mr-2"
            ><i class="flaticon2-plus"></i>
            {{ $t("STOCK_SCRAPPING") }}
          </router-link>
          <router-link
            v-permission="['stock-actions-create']"
            to="testing"
            class="btn btn-light-success font-weight-bolder mr-2"
            ><i class="flaticon2-plus"></i>
            {{ $t("STOCK_TESTING") }}
          </router-link>
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          @do-search="doSearch"
          @do-clear="doClear"
        >
          <template v-slot:prepend>
            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'FROM',
                  placeholder: $t('FROM')
                }"
                :date.sync="filters.fromDate"
              ></FormDatepickerBase>
            </b-col>

            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'TO',
                  placeholder: $t('TO')
                }"
                :date.sync="filters.toDate"
              ></FormDatepickerBase>
            </b-col>
            <b-col
              :lg="4"
              :md="12"
              v-if="!isPageLoading && _.size(stockActionTypes) > 0"
            >
              <v-select
                :items="stockActionTypes"
                :label="$t('STOCK_ACTION_TYPE_NAME')"
                item-text="name"
                item-value="id"
                hide-details
                clearable
                v-model="filters.stockActionTypeId"
                @click:clear="clearFilter('stockActionTypeId')"
              ></v-select>
            </b-col>
            <b-col :lg="4" :md="12">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'DOC_DATE',
                  placeholder: $t('DOC_DATE')
                }"
                :date.sync="filters.docDate"
              ></FormDatepickerBase>
            </b-col>
            <b-col :lg="4" :md="12">
              <v-text-field
                v-model="filters.docNum"
                :label="$t('DOC_NUM')"
                single-line
                hide-details
              ></v-text-field>
            </b-col>
            <b-col :lg="4" :md="12" v-if="!isPageLoading && _.size(stores) > 0">
              <v-autocomplete
                :items="stores"
                :label="$t('STORE')"
                item-text="name"
                item-value="id"
                clearable
                v-model="filters.storeId"
                @click:clear="clearFilter('storeId')"
              ></v-autocomplete>
            </b-col>
            <b-col
              :lg="4"
              :md="12"
              v-if="!isPageLoading && _.size(suppliers) > 0"
            >
              <v-autocomplete
                :items="suppliers"
                :label="$t('SUPPLIERS')"
                item-text="name"
                item-value="id"
                clearable
                v-model="filters.supplierId"
                @click:clear="clearFilter('supplierId')"
              ></v-autocomplete>
            </b-col>
            <b-col
              :lg="4"
              :md="12"
              v-if="!isPageLoading && _.size(suppliers) > 0"
            >
              <v-autocomplete
                :items="clients"
                :label="$t('CLIENTS')"
                item-text="name"
                item-value="id"
                clearable
                v-model="filters.clientId"
                @click:clear="clearFilter('clientId')"
              ></v-autocomplete>
            </b-col>
          </template>
        </SearchTools>
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          sort-by="forDate"
          sort-desc="true"
          :loading="isLoadingStock"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
            itemsPerPageText: $t('ITEMS_PER_PAGE_TEXT')
          }"
          @update:options="getDataFromApi()"
          class="elevation-1"
        >
          <template v-slot:item.name="{ item }">
            <strong>{{ item.warehouseName }}</strong> | {{ item.name }}
            <small>({{ item.unitShortName }})</small>
          </template>
          <template v-slot:item.stockActionItem.unitValue="{ item }">
            <strong>{{ item.stockActionItem.unitValue }}</strong>
            <small> ({{ item.unitShortName }})</small>
          </template>
          <template v-slot:item.forDate="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['stock-actions-show'])"
              :to="{
                name: 'stock-action-review',
                params: { id: item.stockActionId }
              }"
            >
              {{ item.forDate | formatDate }}
            </router-link>
            <template v-else>{{ item.forDate | formatDate }}</template>
          </template>
          <template v-slot:item.stockActionItem.lot="{ item }">
            {{ item.stockActionItem.lot }}<br />
            ({{ item.stockActionItem.lotExpDate }})
          </template>

          <template v-slot:item.stockActionTypeName="{ item }">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <div color="primary" dark v-bind="attrs" v-on="on">
                  <i :class="getIconOperator(item.stockActionTypeOperator)"></i>

                  <router-link
                    v-if="checkIfUserHasPermission(['stock-actions-show'])"
                    :to="{
                      name: 'stock-action-review',
                      params: { id: item.stockActionId }
                    }"
                  >
                    {{ item.stockActionTypeName }}
                  </router-link>
                  <template v-else>{{ item.stockActionTypeName }}</template>
                </div>
              </template>
              <span>
                <small v-if="_.size(item.scrapping) > 0">
                  {{ item.scrapping.stockScrappingReasonName }}
                  {{ item.scrapping.stockScrappingTypeName }}</small
                >
                <small v-if="_.size(item.testing) > 0">
                  {{ item.testing.stockTestingReasonName }}</small
                >
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.stockActionItem.totalPrice="{ item }">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <strong color="primary" dark v-bind="attrs" v-on="on">{{
                  item.stockActionItem.totalPrice
                }}</strong>
              </template>
              <span>
                <small>
                  ({{ item.stockActionItem.unitValue
                  }}{{ item.unitShortName }} *
                  {{ item.stockActionItem.singlePrice }} +
                  {{ item.stockActionItem.transportTotalPrice }} +
                  {{ item.stockActionItem.customsTotalFee }})</small
                ></span
              >
            </v-tooltip>
          </template>
          <template v-slot:item.supplierName="{ item }">
            {{
              item.stockSupplierPurchase
                ? item.stockSupplierPurchase.supplierName
                : ""
            }}
            {{ item.stockWholesale ? item.stockWholesale.clientName : "" }}

            <div
              v-if="
                item.stockActionTypeId == 11 || item.stockActionTypeId == 12
              "
            >
              {{ item.clientName }}
            </div>

            <router-link
              v-if="item.stockActionTypeId == 2 && item.stockPurchaseOrder"
              :to="{
                name: 'stock-purchase-order-review',
                params: { id: item.stockPurchaseOrder.id }
              }"
            >
              ({{ item.stockPurchaseOrder.id }})
              {{ item.stockPurchaseOrder.toStoreName }}</router-link
            >
            <router-link
              v-if="item.stockActionTypeId == 4 && item.stockPurchaseOrder"
              :to="{
                name: 'stock-purchase-order-review',
                params: { id: item.stockPurchaseOrder.id }
              }"
            >
              ({{ item.stockPurchaseOrder.id }})
              {{ item.stockPurchaseOrder.fromStoreName }}</router-link
            >
            <router-link
              v-if="item.stockActionTypeId == 9 && item.stockPurchaseOrder"
              :to="{
                name: 'stock-purchase-order-review',
                params: { id: item.stockPurchaseOrder.id }
              }"
            >
              ({{ item.stockPurchaseOrder.id }})

              <span v-if="item.storeId == item.stockPurchaseOrder.toStoreId">{{
                item.stockPurchaseOrder.fromStoreName
              }}</span>
              <span v-else>{{ item.stockPurchaseOrder.toStoreName }}</span>
            </router-link>
          </template>
          <template slot="body.append">
            <tr class="previewTr">
              <th colspan="100%">
                {{ $t("TOTAL_VALUE") }}:
                {{ allItemsTotalPriceSum | formatBalance }}
              </th>
            </tr>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
      <v-overlay :value="!firstLoader && isLoadingSharedStore">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <template v-slot:footer>
        <div class="text-right">
          <v-dialog
            :retain-focus="false"
            v-model="dialogIssuedDocs"
            max-width="900px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular
                v-show="isIssuedDocsLoading"
                indeterminate
                color="green"
              ></v-progress-circular>
              <v-btn
                v-show="!isIssuedDocsLoading"
                color="btn-light-success"
                class="btn btn-light-success font-weight-bolder mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <i class="far fa-file-excel"></i>
                {{ $t("DOWNLOAD_ISSUED_DOCUMENTS") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{
                  $t("DOWNLOAD_ISSUED_DOCUMENTS")
                }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <FormDatepicker
                        v-model="dates"
                        :type="'text'"
                        :clearable="true"
                        :i18n="{
                          label: 'DATE'
                        }"
                      ></FormDatepicker>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <v-autocomplete
                        :items="stores"
                        :label="$t('STORE')"
                        item-text="name"
                        item-value="id"
                        hide-details
                        clearable
                        v-model="filters.storeId"
                        @click:clear="clearFilter('storeId')"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Затвори</v-btn
                >
                <v-btn
                  color="blue darken-1"
                  text
                  @click="getIssuedDocumentsFiles"
                >
                  Презареди списък</v-btn
                >
                <v-btn
                  :disabled="filters.storeId == null || _.size(dates) == 0"
                  color="blue darken-1"
                  text
                  @click="getIssuedDocuments()"
                >
                  Генерирай
                </v-btn>
              </v-card-actions>
              <v-card-text>
                <h3 v-show="messageExpot">{{ messageExpot }}</h3>
                <v-card v-if="showFiles">
                  <v-list>
                    <v-list-item-title class="ml-4"
                      ><v-icon @click="getIssuedDocumentsFiles"
                        >mdi-refresh</v-icon
                      >
                      Генерирани документи
                    </v-list-item-title>
                    <v-list-item
                      v-for="(file, i) in files"
                      :key="i"
                      :value="file"
                      active-color="primary"
                    >
                      <i class="far fa-file-excel"></i>
                      <a :href="file.url" download class="ml-2">
                        <v-list-item-title
                          v-text="file.name"
                        ></v-list-item-title>
                      </a>
                      <v-list-item-title class="ml-5">
                        {{ file.time | formatDateTime }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-card-text>
            </v-card>
          </v-dialog>
          <v-dialog :retain-focus="false" v-model="dialogXML" max-width="900px">
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular
                v-show="isXMLloading"
                indeterminate
                color="green"
              ></v-progress-circular>
              <v-btn
                v-show="!isXMLloading"
                color="btn-light-success"
                class="btn btn-light-success font-weight-bolder mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <i class="far fa-file-excel"></i>
                {{ $t("DOWNLOAD_INCOMING_CONTROL") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{
                  $t("DOWNLOAD_INCOMING_CONTROL")
                }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <FormDatepicker
                        v-model="dates"
                        :type="'text'"
                        :clearable="true"
                        :i18n="{
                          label: 'DATE'
                        }"
                      ></FormDatepicker>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <v-autocomplete
                        :items="stores"
                        :label="$t('STORE')"
                        item-text="name"
                        item-value="id"
                        clearable
                        v-model="filters.storeId"
                        v-on:change="doSelectStore"
                        @click:clear="clearFilter('storeId')"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Затвори
                </v-btn>
                <v-btn
                  :disabled="filters.storeId == null || _.size(dates) == 0"
                  color="blue darken-1"
                  text
                  @click="getXML()"
                >
                  Генерирай
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog
            :retain-focus="false"
            v-model="dialogPrice"
            max-width="500px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular
                v-show="isXMLloading"
                indeterminate
                color="green"
              ></v-progress-circular>
              <v-btn
                v-show="!isXMLloading"
                color="btn-light-success"
                class="btn btn-light-success font-weight-bolder mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <i class="far fa-file-excel"></i>
                {{ $t("DOWNLOAD_ITEMS_PRICE_EXCEL") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{
                  $t("DOWNLOAD_ITEMS_PRICE_EXCEL")
                }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <v-autocomplete
                        :items="stores"
                        :label="$t('STORE')"
                        item-text="name"
                        item-value="id"
                        hide-details
                        clearable
                        v-model="filters.storeId"
                        @click:clear="clearFilter('storeId')"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Затвори
                </v-btn>
                <v-btn
                  :disabled="filters.storeId == null"
                  color="blue darken-1"
                  text
                  @click="getPriceXML()"
                >
                  Генерирай
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog
            :retain-focus="false"
            v-model="dialogMZxml"
            max-width="900px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular
                v-show="isXmlMzLoading"
                indeterminate
                color="green"
              ></v-progress-circular>
              <v-btn
                v-show="!isXmlMzLoading"
                color="btn-light-success"
                class="btn btn-light-success font-weight-bolder mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <i class="far fa-file-excel"></i>
                {{ $t("DOWNLOAD_XLS_INVENTORY_LOG_DETAILED") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{
                  $t("DOWNLOAD_XLS_INVENTORY_LOG_DETAILED")
                }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <FormDatepicker
                        v-model="dates"
                        :type="'text'"
                        :clearable="true"
                        :i18n="{
                          label: 'DATE'
                        }"
                      ></FormDatepicker>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <v-autocomplete
                        :items="stores"
                        :label="$t('STORE')"
                        item-text="name"
                        item-value="id"
                        hide-details
                        clearable
                        v-model="filters.storeId"
                        @click:clear="clearFilter('storeId')"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <v-autocomplete
                        :items="stockActionItems"
                        :label="$t('ITEMS')"
                        item-text="name"
                        item-value="id"
                        hide-details
                        clearable
                        v-model="filters.selectedItemIds"
                        @click:clear="clearFilter('selectedItemIds')"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Cancel
                </v-btn>
                <v-btn
                  :disabled="
                    filters.storeId == null ||
                      _.size(dates) == 0 ||
                      filters.selectedItemIds == null
                  "
                  color="blue darken-1"
                  text
                  @click="getMzXML()"
                >
                  Генерирай
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <router-link
            v-permission="['stock-actions-create']"
            to="stock-reference-summary"
          >
            <v-btn
              v-show="!isXmlMzLoading"
              color="btn-light-success"
              class="btn btn-light-success font-weight-bolder mr-2"
            >
              <i class="far fa-file-excel"></i>
              {{ $t("STOCK_REFERENCE_SUMMARY_DOC") }}
            </v-btn>
          </router-link>
          <router-link
            v-permission="['stock-actions-create']"
            to="stock-expense"
          >
            <v-btn
              v-show="!isXmlMzLoading"
              color="btn-light-success"
              class="btn btn-light-success font-weight-bolder mr-2"
            >
              <i class="far fa-file-excel"></i>
              {{ $t("STOCK_EXPENSE_DOC") }}
            </v-btn>
          </router-link>
          <router-link
            v-permission="['stock-actions-show']"
            to="stock-consumptions"
          >
            <v-btn
              v-show="!isXmlMzLoading"
              color="btn-light-success"
              class="btn btn-light-success font-weight-bolder mr-2"
            >
              <i class="far fa-file"></i>
              {{ $t("STOCK_CONSUMPTIONS") }}
            </v-btn>
          </router-link>
        </div>
      </template>
    </b-card>
  </div>
</template>
<style scoped>
tr.previewTr th {
  font-size: 18px !important;
}
</style>
<script>
//General
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import FormDatepickerBase from "@/view/content/forms/components/FormDatepickerBase.vue";

//Audit Module
import {
  FETCH_STOCK_ACTION_ITEMS,
  GET_INCOMING_CONTROL_XML,
  GET_INVENTORY_LOG_DETAILED_XML,
  GET_ISSUED_DOCUMENTS_FILES,
  GET_ISSUED_DOCUMENTS_XLS,
  GET_LATEST_ITEM_PRICE_XML,
  FETCH_CLIENT_BASIC
} from "@/modules/stock/store/stock.module";

import { FETCH_STORES_BASIC } from "@/core/services/store/shared_store.module";
import { FETCH_SUPPLIERS_BASIC } from "@/core/services/store/shared_supplier.module";

//Shared Modules
import { FETCH_SELECTS_BASIC } from "@/core/services/store/shared_base.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

import _ from "lodash";
import moment from "moment";
import FormDatepicker from "@/view/content/forms/components/FormDatepicker.vue";

export default {
  name: "StockActionItemList",
  mixins: [permissionMixin],
  data() {
    return {
      firstLoader: true,
      isPageLoading: true,
      items: [],
      stores: [],
      files: [],
      showFiles: false,
      messageExpot: false,
      stockActionItems: [],
      suppliers: [],
      clients: [],
      isXMLloading: false,
      isXmlMzLoading: false,
      isIssuedDocsLoading: false,
      dialogXML: false,
      dialogPrice: false,
      dialogIssuedDocs: false,
      showItems: false,
      dialogMZxml: false,
      dates: [],
      totalItems: 100,
      allItemsTotalPriceSum: 0,
      searchQuery: "",
      filters: {
        storeId: null,
        supplierId: null,
        clientId: null,
        fromDate: null,
        toDate: null,
        stockActionTypeId: null,
        docNum: "",
        docDate: null,
        selectedItemIds: []
      },
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 10,
        totalItems: 100
      },
      stockActionTypes: [],
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      headers: [
        {
          text: this.$t("FOR_DATE"),
          value: "forDate"
        },
        {
          text: this.$t("ITEM"),
          align: "start",
          filterable: true,
          value: "name",
          sortable: false
        },
        {
          text: this.$t("QUANTITY"),
          value: "stockActionItem.unitValue",
          sortable: false
        },
        {
          text: this.$t("LOT"),
          value: "stockActionItem.lot",
          sortable: false
        },
        {
          text: this.$t("STOCK_ACTION_TYPE_NAME"),
          value: "stockActionTypeName"
        },
        {
          text: this.$t("STORE"),
          value: "storeName",
          sortable: false
        },
        {
          text: this.$t("COUNTERPARTY"),
          value: "supplierName",
          sortable: false
        },
        {
          text: this.$t("TOTAL_VALUE"),
          value: "stockActionItem.totalPrice",
          align: "right",
          sortable: false
        }
      ]
    };
  },
  components: {
    FormDatepicker,
    SearchTools,
    FormDatepickerBase
  },
  watch: {
    dialogXML(val) {
      val || this.close();
    },
    dialogPrice(val) {
      val || this.close();
    },
    dialogIssuedDocs(val) {
      this.getIssuedDocumentsFiles();
      val || this.close();
    },
    dialogMZxml(val) {
      val || this.close();
    },
    storesParams(value) {
      let vm = this;
    },
    suppliersParams(value) {
      let vm = this;
    }
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Stock Actions", route: { name: "stock-actions-list" } },
      { title: "Stock Actions List" }
    ]);

    vm.getStores();
    vm.getSuppliers();
    vm.getClients();
    vm.getSelects();
    vm.getIssuedDocumentsFiles();
  },
  computed: {
    ...mapGetters([
      "getItems",
      "isLoadingStock",
      "isLoadingSharedStore",
      "currentUser"
    ]),
    params() {
      return {
        ...this.options,
        query: this.searchQuery,
        storeId: this.filters.storeId,
        supplierId: this.filters.supplierId,
        clientId: this.filters.clientId,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        stockActionTypeId: this.filters.stockActionTypeId,
        docNum: this.filters.docNum,
        docDate: this.filters.docDate,
        itemIds: this.filters.selectedItemIds
      };
    },
    storesParams() {
      let storesParams = {};
      return storesParams;
    },
    suppliersParams() {
      let suppliersParams = {};
      return suppliersParams;
    },
    tableHeaders() {
      let vm = this;

      return vm.headers;
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    }
  },
  methods: {
    close() {
      let vm = this;
      vm.dialogXML = false;
      vm.dialogPrice = false;
      vm.dialogIssuedDocs = false;
      vm.dialogMZxml = false;
    },
    clearFilter(key) {
      let vm = this;
      vm.$nextTick(function() {
        this.$set(vm.filters, key, null);
      });
    },
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      vm.filters.storeId = null;
      vm.filters.supplierId = null;
      vm.filters.clientId = null;
      vm.filters.stockActionTypeId = null;
      vm.filters.docNum = "";
      vm.filters.docDate = null;
      vm.filters.selectedItemIds = [];
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getXML() {
      let vm = this;
      vm.isPDFloading = true;
      //copy current params to modify
      vm.isLoadingStoreDailyTasks = true;
      let params = this.params;
      params.dateRange = vm.dates;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(GET_INCOMING_CONTROL_XML, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            let blob = new Blob([data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "income-control-" + vm.dates;
            link.click();
            link.remove();
            vm.dialogXML = false;
            if (vm.isPageLoading == true || vm.isXMLloading == true) {
              vm.isPageLoading = false;
              vm.isXMLloading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getPriceXML() {
      let vm = this;
      vm.isPDFloading = true;
      //copy current params to modify
      vm.isLoadingStoreDailyTasks = true;
      let params = this.params;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(GET_LATEST_ITEM_PRICE_XML, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            let blob = new Blob([data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "latest-items-price-" + vm.dates;
            link.click();
            link.remove();
            vm.dialogPrice = false;
            if (vm.isPageLoading == true || vm.isXMLloading == true) {
              vm.isPageLoading = false;
              vm.isXMLloading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getMzXML() {
      let vm = this;
      vm.isXmlMzLoading = true;
      //copy current params to modify
      vm.isLoadingStoreDailyTasks = true;
      let params = {
        dateRange: vm.dates,
        storeId: this.params.storeId,
        itemIds: this.params.itemIds
      };
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(GET_INVENTORY_LOG_DETAILED_XML, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            let blob = new Blob([data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "inventory-log-detailed-" + vm.dates;
            link.click();
            link.remove();
            vm.dialogMZxml = false;
            if (vm.isPageLoading == true || vm.isXmlMzLoading == true) {
              vm.isPageLoading = false;
              vm.isXmlMzLoading = false;
            }
          });
        })
        .catch(error => {
          let responseErrors = JSON.parse(
            new TextDecoder().decode(error.response.data)
          );
          if (error.response.status === 400) {
            vm.firstLoader = false;
            vm.isXmlMzLoading = false;
            //copy current params to modify
            vm.isLoadingStoreDailyTasks = false;
            vm.dialogMZxml = false;
            vm.$notify({
              group: "notify",
              type: "error",
              text: responseErrors.message
            });
          }
          if (error.response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: responseErrors.message
            });
          }
        });
    },
    getIssuedDocuments() {
      let vm = this;
      vm.isIssuedDocsLoading = true;
      vm.isLoadingStoreDailyTasks = true;
      let params = {
        dateRange: vm.dates,
        storeId: this.params.storeId,
        itemIds: this.params.itemIds
      };
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(GET_ISSUED_DOCUMENTS_XLS, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            vm.messageExpot = data.data.message;
            setTimeout(function() {
              vm.getIssuedDocumentsFiles();
              if (vm.isPageLoading == true || vm.isIssuedDocsLoading == true) {
                vm.isPageLoading = false;
                vm.isIssuedDocsLoading = false;
                vm.messageExpot = false;
              }
            }, 5000);
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getIssuedDocumentsFiles() {
      let vm = this;
      this.$store
        .dispatch(GET_ISSUED_DOCUMENTS_FILES)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            if (data.length > 0) {
              vm.showFiles = true;
              vm.files = data;
            } else {
              vm.showFiles = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getDataFromApi() {
      let vm = this;
      //copy current params to modify
      let params = this.params;

      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      let apiParams = vm.transformUrlParams(params);
      // console.log(apiParams);
      vm.$store
        .dispatch(FETCH_STOCK_ACTION_ITEMS, apiParams)
        .then(data => {
          // vm.$notify({
          //   group: "notify",
          //   type: "success",
          //   title: "<i class='flaticon2-checkmark'></i> Success",
          //   text: data.message
          // });
          vm.firstLoader = false;
          vm.$nextTick(function() {
            vm.allItemsTotalPriceSum = 0;
            vm.items = data.data.items;
            vm.totalItems = data.data.totalItemsCount;
            if (_.size(data.data.additionalData) > 0) {
              vm.allItemsTotalPriceSum =
                data.data.additionalData.allItemsTotalPriceSum;
            }

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "stock-actions-list" });
          }
        });
    },
    getStores() {
      let vm = this;
      let params = this.storesParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_STORES_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "stores", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    getSuppliers() {
      let vm = this;
      let params = this.suppliersParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_SUPPLIERS_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "suppliers", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    getClients() {
      let vm = this;
      let params = this.suppliersParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_CLIENT_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "clients", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    getSelects() {
      let vm = this;
      let payload = {
        stock_action_types: {
          fields: ["id", "name"],
          search: ""
        },
        items: {
          fields: ["id", "name"],
          search: ""
        }
      };
      this.$store
        .dispatch(FETCH_SELECTS_BASIC, payload)
        .then(data => {
          vm.stockActionTypes = data.data.stock_action_types;
          vm.stockActionItems = data.data.items;
        })
        .catch(response => {
          console.log(response);
        });
    },
    getIconOperator(operator) {
      if (operator == "+") {
        return "flaticon2-plus text-success";
      }

      if (operator == "-") {
        return "flaticon2-line text-danger";
      }
      return "";
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    },
    handleFilterInput(data) {
      let vm = this;
      this.$set(vm.filters, data.prop, data.value);
    }
  }
};
</script>
